import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AlertCircle, Loader2 } from "lucide-react";

/**
 * /auth/magic-link — landing page for a magic-link email click.
 *
 * Reads `?token=…`, POSTs it to /api/auth/magic-link/verify, refreshes
 * the AuthContext (so the session cookie the server just set becomes
 * visible to the app), and navigates to the server-supplied
 * `redirect_to` (typically /uni-portal/uni/{id} after a successful
 * uni claim, or /uni-portal for a generic sign-in).
 *
 * StrictMode-safe via a module-level cache keyed by the token
 * string. A `useRef` doesn't survive StrictMode's mount → unmount →
 * remount cycle (each mount gets a fresh ref), so the two effect
 * runs would each fire the POST and the second call would race and
 * hit the atomic "already used" guard on the backend. The
 * module-scoped Map persists across mounts within the same JS
 * runtime, deduping the request while still failing cleanly on a
 * genuinely-reused token (page refresh, second click, etc.) because
 * that path never populates the cache and the backend still enforces
 * single-use.
 */
const inflight = new Map();  // token -> Promise<{data, error}>

export default function AuthMagicLink() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = params.get("token");
    if (!token) {
      setError("No sign-in token found in the URL.");
      return;
    }
    let cancelled = false;
    let promise = inflight.get(token);
    if (!promise) {
      // First mount of this token in this JS runtime — kick off the
      // verify POST and stash the promise so a StrictMode remount (or
      // any other component that happens to render this route) shares
      // it instead of racing.
      promise = (async () => {
        try {
          const { data } = await api.post("/auth/magic-link/verify", { token });
          return { data, error: null };
        } catch (err) {
          const msg = err?.response?.data?.detail
            || "This sign-in link is invalid or has expired.";
          return { data: null, error: String(msg) };
        }
      })();
      inflight.set(token, promise);
    }
    (async () => {
      const { data, error: err } = await promise;
      if (cancelled) return;
      if (err) {
        setError(err);
      } else {
        await refresh();
        toast.success("Signed in.");
        nav(data?.redirect_to || "/uni-portal", { replace: true });
      }
    })();
    return () => { cancelled = true; };
  }, [params, nav, refresh]);

  if (error) {
    return (
      <div
        className="max-w-lg mx-auto px-6 py-24 text-center"
        data-testid="magic-link-error"
      >
        <AlertCircle className="h-8 w-8 mx-auto text-rose-600" />
        <h1 className="mt-3 font-editorial text-2xl text-stone-900">
          Sign-in failed
        </h1>
        <p className="mt-2 text-sm text-stone-600">{error}</p>
        <p className="mt-6 text-xs text-stone-500">
          Head back to the university claim page and request a fresh
          link — the previous one is now invalid.
        </p>
      </div>
    );
  }

  return (
    <div
      className="min-h-[60vh] flex items-center justify-center"
      data-testid="magic-link-loading"
    >
      <div className="text-center">
        <Loader2 className="h-5 w-5 animate-spin inline mr-2 text-stone-500" />
        <div className="label-uppercase inline">Signing you in…</div>
        <div className="font-editorial text-2xl mt-2">Just a moment.</div>
      </div>
    </div>
  );
}
